import type { PageViewData } from '../../tracking/tealium';

type LooselyTypedUtag = {
  gdpr: Record<string, any> & {
    showConsentPreferences: () => void;
  };
  view: (_data?: any) => void;
  link: (_data?: any) => void;
  data?: Partial<PageViewData>;
};

type DataObject = {
  tealium_event?: string | string[];
  country_code?: string | string[];
  currency_code?: string | string[];
  page_name?: string | string[];
  page_type?: string | string[];
  product_id?: string | string[];
  product_name?: string | string[];
  product_category?: string | string[];
  product_quantity?: string | string[];
  product_unit_price?: string | string[];
  cart_total_items?: string | string[];
  cart_subtotal?: string | string[];
  search_results?: string | string[];
  search_keyword?: string | string[];
  [key: string]: undefined | string | string[];
};

export type UtagConsentCategory =
  | 'analytics'
  | 'affiliates'
  | 'display_ads'
  | 'search'
  | 'email'
  | 'personalization'
  | 'social'
  | 'big_data'
  | 'misc'
  | 'cookiematch'
  | 'cdp'
  | 'mobile'
  | 'engagement'
  | 'monitoring'
  | 'crm';

// Expand the definition by inspecting the Tealium API:
// https://docs.tealium.com/platforms/javascript/api/
// Unfortunately, there is no preexisting typescript type.
export type Utag = {
  gdpr: {
    getCategories: (_onlyEnabledCategories?: boolean) => UtagConsentCategory[];
    getSelectedCategories: () => UtagConsentCategory[];
    showConsentPreferences: (_languageCodeWithRegion?: string) => void;
    setAllCategories: (_state: boolean, _noCollect?: boolean) => void;
    setPreferencesFromList: (_list: UtagConsentCategory[]) => void;
    setPreferencesValues: (
      _state: Record<UtagConsentCategory, '0' | '1'>,
      _noCollect?: boolean,
    ) => void;
  };
  link: (_data: DataObject, _callback?: () => void, _uidArray?: string[]) => void;
  track: (_data: DataObject, _callback?: () => void, _uidArray?: string[]) => void;
  view: (_data: DataObject, _callback?: () => void, _uidArray?: string[]) => void;
};

type WindowWithTealium = Window & {
  utag_cfg_ovrd?: {
    noview?: boolean;
  };
  utag?: Utag;
};

// @revisit window.utag declaration (better in …/src/types/index.d.ts?)
// TODO: Either we keep it simple as is, or we get global type declarations across the monorepo working.
export const getLooselyTypedUtag = (
  window: undefined | Window | WindowWithTealium,
): LooselyTypedUtag => {
  if (window && 'utag' in window && window.utag) {
    return window.utag;
  }

  return {
    gdpr: {
      showConsentPreferences: () => {},
      getSelectedCategories: () => [],
    },
    view: () => {},
    link: () => {},
  };
};

export const getUtag = (
  window: undefined | Window | WindowWithTealium,
): Utag | undefined => {
  if (window && 'utag' in window && window.utag) {
    return window.utag;
  }

  return undefined;
};
